import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mainNav } from "@/mocks/navigation";
import { useStore } from "@/store/StoreContext";
import MobileMenu from "./MobileMenu";

export type PanelKey = "search" | "favorites" | "bag";

interface HeaderProps {
  onOpenPanel: (panel: PanelKey) => void;
  overlay?: boolean;
}

export default function Header({ onOpenPanel, overlay = false }: HeaderProps) {
  const { cartCount, favorites } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const solid = scrolled || menuOpen || !overlay;

  const openPanel = (panel: PanelKey) => {
    setMenuOpen(false);
    onOpenPanel(panel);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-8">
        <Link
          to="/"
          aria-label="Amora Belle — página inicial"
          className="flex shrink-0 cursor-pointer items-center gap-2"
          onClick={() => setMenuOpen(false)}
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
              solid ? "border-primary-300 bg-primary-50" : "border-background-50/60 bg-background-50/15"
            }`}
          >
            <i className={`ri-leaf-line text-base ${solid ? "text-primary-600" : "text-background-50"}`} />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-2xl font-semibold tracking-wide transition-colors md:text-[28px] ${
                solid ? "text-primary-700" : "text-background-50"
              }`}
            >
              Amora Belle
            </span>
            <span
              className={`mt-0.5 hidden text-[10px] uppercase tracking-[0.22em] transition-colors sm:block ${
                solid ? "text-foreground-600" : "text-background-100/90"
              }`}
            >
              moda feminina
            </span>
          </span>
        </Link>

        <nav aria-label="Categorias" className="hidden items-center gap-5 xl:flex">
          {mainNav.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className={`cursor-pointer whitespace-nowrap text-sm tracking-wide transition-colors ${
                solid ? "text-foreground-700 hover:text-primary-600" : "text-background-50 hover:text-secondary-300"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <button
            type="button"
            onClick={() => openPanel("search")}
            aria-label="Buscar"
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className="ri-search-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => openPanel("favorites")}
            aria-label="Favoritos"
            className={`relative hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors sm:flex ${
              solid ? "text-foreground-700 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className="ri-heart-3-line text-xl" />
            {favorites.length > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[10px] font-medium text-background-50">
                {favorites.length}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => openPanel("bag")}
            aria-label="Sacola"
            className={`relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className="ri-shopping-bag-3-line text-xl" />
            {cartCount > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[10px] font-medium text-background-50">
                {cartCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors xl:hidden ${
              solid ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className="ri-menu-3-line text-2xl" />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} onOpenPanel={onOpenPanel} />
      ) : null}
    </header>
  );
}